import { z } from 'zod';

import { ProductMatchStrategy } from './enums.js';
import { listQuerySchema } from './common.js';

/**
 * ÜRÜN ↔ REKLAM KÖPRÜSÜ — Genişleme Adım 3.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KÖPRÜ KURULMADAN ÖNCE ÖLÇÜLENLER
 * ═══════════════════════════════════════════════════════════════════════════
 * Adım 1 mağaza katalogunu getirdi, Faz 1–8 reklam metriklerini. İkisi
 * arasında BAĞ YOKTU ve plan bunun "adımın en zor kısmı" olduğunu söylüyordu.
 * Karar ölçümle verildi, varsayımla değil:
 *
 *  1. Reklam platformu bize ürünün mağaza kimliğini VERMİYOR. Verdiği şey
 *     KATALOG FEED'İNDEKİ kimlik: Meta `retailer_id`, Google
 *     `product_item_id`. O kimliğin biçimini feed'i üreten taraf seçiyor.
 *  2. Shopify'ın resmî Meta/Google satış kanalları o kimliği
 *     `shopify_<ÜLKE>_<ürünId>_<varyantId>` biçiminde üretiyor — yani
 *     ürünün VE varyantın sayısal kimliğini İÇİNDE taşıyor. Bu, ayrıştırılabilir
 *     ve deterministik bir bağ.
 *  3. Eski projede (APEX GrowthOS) köprü İNİŞ URL'İ ve ürün `handle`ı
 *     karşılaştıran bir HEURİSTİKTİ — kodun kendi yorumu onu "HEURİSTİK"
 *     diye işaretlemişti. Üstelik veritabanındaki ürün attribution
 *     satırlarının HEPSİ demo workspace'ten geliyordu: o köprü gerçek bir
 *     hesapta HİÇ koşmamıştı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İSİM/BAŞLIK EŞLEŞTİRMESİ YOK — ve eklenmeyecek
 * ═══════════════════════════════════════════════════════════════════════════
 * "Kırmızı Elbise" adlı iki ürün aynı şey değildir. İsimle eşleşen bir köprü
 * yanlış ürüne harcama yazar ve yanlış olduğu HİÇBİR YERDEN anlaşılmaz —
 * sayılar makul görünür, sadece yanlış ürüne aittir. Eşleşemeyen satır
 * `UNMATCHED` olarak YAZILIR ve raporda ayrı bir büyüklük olarak görünür;
 * sessizce atılmaz, tahmin de edilmez.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Feed kimliğinin ayrıştırılması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `shopify_TR_8873022751024_45123456789` → ülke, ürün, varyant.
 *
 * Ülke segmenti OPSİYONEL: Shopify'ın eski feed'leri `shopify_<pid>_<vid>`
 * biçimini de üretiyor. Varyant segmenti de opsiyonel — ürün seviyesinde
 * kurulmuş feed'lerde yalnız ürün kimliği var.
 *
 * 🔴 `i` bayrağı ZORUNLU. Google Ads raporlarında `segments.product_item_id`
 * KÜÇÜK HARFE ÇEVRİLMİŞ geliyor (`shopify_tr_...`). Büyük/küçük harfe duyarlı
 * bir kalıp, Google tarafındaki HER satırı eşleşmez yapardı — ve bu sessiz
 * olurdu, çünkü "eşleşmedi" geçerli bir sonuç.
 */
const FEED_COMPOSITE_RE = /^shopify_(?:([a-z]{2})_)?(\d+)(?:_(\d+))?$/i;

/// Shopify GID'inin sayısal kuyruğu: `gid://shopify/Product/8873022751024`.
const GID_TAIL_RE = /^gid:\/\/shopify\/\w+\/(\d+)$/;

/// GID'in sayısal kısmını verir. GID değilse `null` — uydurmuyor.
export function gidNumericTail(gid: string): string | null {
  const m = GID_TAIL_RE.exec(gid.trim());
  return m?.[1] ?? null;
}

export interface ParsedFeedRef {
  productNumeric: string;
  variantNumeric: string | null;
}

/// `shopify_…` bileşik kimliğini ayrıştırır. Biçim tutmuyorsa `null`.
export function parseFeedCompositeRef(ref: string): ParsedFeedRef | null {
  const m = FEED_COMPOSITE_RE.exec(ref.trim());
  // Ürün kimliği kalıbın ZORUNLU parçası; yokluğu kalıbın tutmadığı anlamına
  // gelir. Yine de açıkça kontrol ediliyor — `m[2]!` yazmak, kalıp bir gün
  // değişirse hatayı çalışma zamanına ertelerdi.
  const productNumeric = m?.[2];
  if (!productNumeric) return null;
  return { productNumeric, variantNumeric: m?.[3] ?? null };
}

// ═══════════════════════════════════════════════════════════════════════════
// Feed kimliğinin ÜRETİLMESİ — ayrıştırıcının tersi (19 Ağu 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Merchant Center `id` (offer id) üst sınırı — Google ürün veri şartnamesi.
 */
export const OFFER_ID_MAX_LENGTH = 50;

/**
 * Bir teklif kimliğinde güvenle taşınabilecek karakterler.
 *
 * Yalnız KÜÇÜK harf: Google, `segments.product_item_id`yi raporlarda küçük
 * harfe çevirerek döndürüyor. Büyük harf içeren bir kimlik üretmek, kendi
 * bastığımız teklifi kendi raporumuzda tanıyamamak demekti.
 */
const OFFER_ID_SAFE_RE = /^[a-z0-9._~-]+$/;

/// Ülke kodu segmenti — ayrıştırıcının kalıbıyla aynı daralık (iki harf).
const FEED_COUNTRY_RE = /^[A-Za-z]{2}$/;

export type OfferIdRejection = 'EMPTY' | 'UNSAFE_CHARS' | 'TOO_LONG';

export type OfferIdResult =
  | { ok: true; offerId: string; strategy: 'FEED_COMPOSITE' | 'PLATFORM_REF' }
  | { ok: false; reason: OfferIdRejection; attempted: string };

/**
 * Katalogdaki bir varyant için Merchant Center teklif kimliği üretir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 TEK ZORUNLULUK: ürettiğimiz kimlik `matchProductFeedRef` tarafından
 * GERİ ÇÖZÜLEBİLMELİ
 * ═══════════════════════════════════════════════════════════════════════════
 * Aynı kimlik dört yerde birden yaşıyor: bizim bastığımız feed, Google'ın
 * `shopping_performance_view` raporu, ileride GA4 `item_id` ve Meta katalog
 * `content_ids`. Farklı üretilirse ürün performansı raporunda harcama
 * `UNMATCHED` kovasına düşer — ve bu SESSİZ bir arıza, çünkü "eşleşmedi"
 * geçerli bir sonuç.
 *
 * Bu yüzden burada YENİ bir biçim İCAT EDİLMİYOR; eşleyicinin zaten tanıdığı
 * iki biçimden biri üretiliyor:
 *
 *   1. FEED_COMPOSITE — `shopify_<ülke>_<ürünId>_<varyantId>`
 *      Yalnız ürün VE varyant kimliğinin ikisi de Shopify GID'i olduğunda.
 *      Biçim, Shopify'ın KENDİ Google satış kanalının ürettiğiyle aynı; aynı
 *      mağaza iki yoldan bağlandığında kimlikler çakışmasın diye.
 *   2. PLATFORM_REF — varyantın ham `platformRef`i, küçük harfe çevrilmiş.
 *      Pazaryerlerinde GID yok; eşleyicinin küçük harf basamağı bunu çözüyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KISALTMA, HASH'LEME, KARAKTER TEMİZLEME YOK — ürün ELENİR
 * ═══════════════════════════════════════════════════════════════════════════
 * Reddedilen alternatif: güvensiz karakterleri temizleyip ya da 50 karaktere
 * kırpıp yine de basmak. O durumda bastığımız kimlik geri çözülemez hâle
 * gelirdi ve ürün Google'da yayında görünürken raporumuzda hiç var olmazdı —
 * yani en kötü hâl: para harcayan ama ölçülemeyen bir teklif.
 *
 * Hash de aynı sebeple reddedildi: deterministik olurdu ama `matchProductFeedRef`
 * onu çözemezdi.
 *
 * Elenen ürün SESSİZ değil: yayın öncesi kuru koşuda sebebiyle raporlanıyor.
 */
export function buildOfferId(input: {
  productPlatformRef: string;
  variantPlatformRef: string;
  /// Feed'in ülke etiketi (`TR`). Yoksa bileşik kimlik ülkesiz üretilir —
  /// ayrıştırıcı ülke segmentini zaten opsiyonel kabul ediyor.
  feedCountry?: string | null;
}): OfferIdResult {
  const productNumeric = gidNumericTail(input.productPlatformRef);
  const variantNumeric = gidNumericTail(input.variantPlatformRef);

  if (productNumeric && variantNumeric) {
    const cc = input.feedCountry?.trim();
    const country = cc && FEED_COUNTRY_RE.test(cc) ? `${cc.toLowerCase()}_` : '';
    return finish(`shopify_${country}${productNumeric}_${variantNumeric}`, 'FEED_COMPOSITE');
  }

  return finish(input.variantPlatformRef.trim().toLowerCase(), 'PLATFORM_REF');
}

function finish(
  candidate: string,
  strategy: 'FEED_COMPOSITE' | 'PLATFORM_REF',
): OfferIdResult {
  if (candidate === '') return { ok: false, reason: 'EMPTY', attempted: candidate };
  if (!OFFER_ID_SAFE_RE.test(candidate)) {
    return { ok: false, reason: 'UNSAFE_CHARS', attempted: candidate };
  }
  if (candidate.length > OFFER_ID_MAX_LENGTH) {
    return { ok: false, reason: 'TOO_LONG', attempted: candidate };
  }
  return { ok: true, offerId: candidate, strategy };
}

// ═══════════════════════════════════════════════════════════════════════════
// Eşleme dizini ve eşleyici
// ═══════════════════════════════════════════════════════════════════════════

export interface ProductRefTarget {
  productId: string;
  variantId: string | null;
}

/**
 * Katalogun eşleme dizini. Senkron başında BİR KEZ kuruluyor; her feed satırı
 * için veritabanına gitmek, 5.000 satırlık bir günde 5.000 sorgu demekti.
 */
export interface ProductMatchIndex {
  /// Ürün GID'inin sayısal kuyruğu → ürün.
  productByNumeric: Map<string, string>;
  /// Varyant GID'inin sayısal kuyruğu → ürün + varyant.
  variantByNumeric: Map<string, ProductRefTarget>;
  /// Ham `platformRef` (GID dâhil) → ürün.
  productByRef: Map<string, string>;
  /// Ham varyant `platformRef` → ürün + varyant.
  variantByRef: Map<string, ProductRefTarget>;
  /**
   * KÜÇÜK HARFE ÇEVRİLMİŞ `platformRef` → ürün / varyant. (19 Ağu 2026)
   *
   * 🔴 Süs değil, ÖLÇÜLMÜŞ bir kopukluğun tamiri. Google
   * `segments.product_item_id`yi küçük harfe çevirerek döndürüyor. Bileşik
   * `shopify_…` kimliğinde bu sorun değildi (kalıbın `i` bayrağı var), ama
   * PLATFORM_REF basamağı `Map.get` ile TAM eşleşme arıyordu: platform
   * kimliğinde harf geçen her pazaryeri (İkas'ın UUID'si, İdeaSoft'un karışık
   * kodu, Trendyol'un `BNN012` gibi satıcı SKU'su) Google tarafında SESSİZCE
   * eşleşmiyordu — "eşleşmedi" geçerli bir sonuç olduğu için hata da vermiyor,
   * yalnız harcama `UNMATCHED` kovasında birikiyordu.
   *
   * Meta tarafında sorun görünmüyordu çünkü `retailer_id`yi olduğu gibi
   * döndürüyor; yani kusur tek platformda ve tek yönde saklıydı.
   *
   * Aynı kimliği taşıyan iki satır varsa (yalnız büyük/küçük harfle ayrışan
   * iki `platformRef`) anahtar `ambiguousRefs`e düşer ve dizine GİRMEZ —
   * `variantBySku`daki çift SKU kuralının birebir aynısı ve aynı gerekçeyle:
   * hangisine yazılacağı bilinmiyorsa yazı tura atılmaz.
   */
  productByRefLower: Map<string, string>;
  variantByRefLower: Map<string, ProductRefTarget>;
  /// Yalnız büyük/küçük harfle ayrıştığı için dizine girmeyen kimlikler.
  ambiguousRefs: Set<string>;
  /**
   * KÜÇÜK HARFE ÇEVRİLMİŞ SKU → ürün + varyant.
   *
   * 🔴 Anahtar küçük harf çünkü Google `product_item_id`yi küçük harfe
   * çevirerek döndürüyor; büyük/küçük harfe duyarlı bir dizin o platformda
   * hiçbir SKU'yu bulamazdı.
   *
   * 🔴 ÇİFT SKU DİZİNE GİRMEZ. Aynı SKU'yu taşıyan iki varyant varsa hangisine
   * yazılacağı BİLİNMİYOR demektir; ikisinden birini seçmek, harcamayı yazı
   * tura ile bölüştürmek olurdu. Belirsiz SKU dizinden düşürülüyor ve satır
   * `UNMATCHED` kalıyor. `ambiguousSkus` bunu raporlanabilir kılıyor.
   */
  variantBySku: Map<string, ProductRefTarget>;
  /// Belirsizliği yüzünden dizine girmeyen SKU'lar (küçük harf).
  ambiguousSkus: Set<string>;
}

export interface ProductMatchResult {
  productId: string | null;
  variantId: string | null;
  strategy: ProductMatchStrategy;
}

const UNMATCHED: ProductMatchResult = {
  productId: null,
  variantId: null,
  strategy: ProductMatchStrategy.UNMATCHED,
};

/**
 * Feed kimliğini katalogdaki ürün/varyanta çözer.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SIRALI ZİNCİR — güvenilirlik azalarak
 * ═══════════════════════════════════════════════════════════════════════════
 *   1. FEED_COMPOSITE  `shopify_<ÜLKE>_<pid>_<vid>` ayrıştırması
 *   2. PLATFORM_REF    kimliğin kendisi ürün/varyant kimliği (GID ya da ham
 *                      sayısal) — elle kurulmuş feed'lerde sık
 *   3. SKU             tam SKU, YALNIZ tekilse
 *   4. UNMATCHED       hiçbiri
 *
 * Her basamak bir ÖNCEKİNDEN daha zayıf; ilk tutan kazanıyor ve hangisinin
 * tuttuğu satıra yazılıyor ki rapor "bu satırın güveni ne" diyebilsin.
 *
 * ⚠️ Varyant bulunamayıp ürün bulunduğunda `variantId` `null` dönüyor ve bu
 * bir HATA DEĞİL: feed ürün seviyesinde kurulmuş olabilir, ya da varyant
 * silinmiş olabilir. Ürün bazlı rapor yine çalışıyor, yalnız varyant kırılımı
 * o satır için yok.
 */
export function matchProductFeedRef(
  rawRef: string,
  index: ProductMatchIndex,
): ProductMatchResult {
  const ref = rawRef.trim();
  if (ref === '') return UNMATCHED;

  // ── 1) Bileşik feed kimliği ───────────────────────────────────────────────
  const parsed = parseFeedCompositeRef(ref);
  if (parsed) {
    if (parsed.variantNumeric) {
      const hit = index.variantByNumeric.get(parsed.variantNumeric);
      if (hit) {
        return { ...hit, strategy: ProductMatchStrategy.FEED_COMPOSITE };
      }
    }
    // Varyant tutmadı ama ürün tutabilir: varyant silinmiş ya da feed ürün
    // seviyesinde. Ürünü bulmak, satırı çöpe atmaktan iyi.
    const productId = index.productByNumeric.get(parsed.productNumeric);
    if (productId) {
      return {
        productId,
        variantId: null,
        strategy: ProductMatchStrategy.FEED_COMPOSITE,
      };
    }
    // Biçim tuttu ama katalogda karşılığı yok — feed'de olup katalogda olmayan
    // bir ürün. Zinciri SÜRDÜRMÜYORUZ: biçim `shopify_…` ise kimlik zaten
    // SKU değil, SKU dizinine sormak yanlış eşleşme üretebilirdi.
    return UNMATCHED;
  }

  // ── 2) Kimliğin kendisi platform kimliği ─────────────────────────────────
  const variantByRef = index.variantByRef.get(ref);
  if (variantByRef) {
    return { ...variantByRef, strategy: ProductMatchStrategy.PLATFORM_REF };
  }
  const productByRef = index.productByRef.get(ref);
  if (productByRef) {
    return {
      productId: productByRef,
      variantId: null,
      strategy: ProductMatchStrategy.PLATFORM_REF,
    };
  }
  // Kimlik büyük/küçük harf sapmasıyla gelmiş olabilir — Google raporu her
  // `product_item_id`yi küçük harfe çeviriyor. Belirsiz olanlar dizinde YOK,
  // yani buradan yanlış eşleşme çıkamaz.
  const lower = ref.toLowerCase();
  if (!index.ambiguousRefs.has(lower)) {
    const variantLower = index.variantByRefLower.get(lower);
    if (variantLower) {
      return { ...variantLower, strategy: ProductMatchStrategy.PLATFORM_REF };
    }
    const productLower = index.productByRefLower.get(lower);
    if (productLower) {
      return {
        productId: productLower,
        variantId: null,
        strategy: ProductMatchStrategy.PLATFORM_REF,
      };
    }
  }

  // Ham sayısal kimlik (`8873022751024`) — GID'siz feed'ler böyle yazıyor.
  if (/^\d+$/.test(ref)) {
    const variantNumeric = index.variantByNumeric.get(ref);
    if (variantNumeric) {
      return { ...variantNumeric, strategy: ProductMatchStrategy.PLATFORM_REF };
    }
    const productNumeric = index.productByNumeric.get(ref);
    if (productNumeric) {
      return {
        productId: productNumeric,
        variantId: null,
        strategy: ProductMatchStrategy.PLATFORM_REF,
      };
    }
    // Sayısal bir kimlik SKU olarak aranmaz: "12345" hem ürün kimliği hem
    // SKU olabilir ve ikisi FARKLI ürünler olabilir.
    return UNMATCHED;
  }

  // ── 3) SKU — yalnız tekilse ──────────────────────────────────────────────
  const sku = ref.toLowerCase();
  if (index.ambiguousSkus.has(sku)) return UNMATCHED;
  const bySku = index.variantBySku.get(sku);
  if (bySku) return { ...bySku, strategy: ProductMatchStrategy.SKU };

  return UNMATCHED;
}

/**
 * Dizini kurar. Girdiler katalogdan okunmuş ham satırlar.
 *
 * `sku` iki kez geziliyor (önce say, sonra yaz) çünkü belirsizlik ancak
 * TÜM varyantlar görüldükten sonra bilinebiliyor: üçüncü varyantta ortaya
 * çıkan bir çakışma, ilk ikisinin dizine girmiş olmasını da geçersiz kılar.
 */
export function buildProductMatchIndex(
  products: { id: string; platformRef: string }[],
  variants: { id: string; productId: string; platformRef: string; sku: string | null }[],
): ProductMatchIndex {
  const index: ProductMatchIndex = {
    productByNumeric: new Map(),
    variantByNumeric: new Map(),
    productByRef: new Map(),
    variantByRef: new Map(),
    productByRefLower: new Map(),
    variantByRefLower: new Map(),
    ambiguousRefs: new Set(),
    variantBySku: new Map(),
    ambiguousSkus: new Set(),
  };

  // Küçük harf çakışması ürün ve varyant kimliklerinde AYRI AYRI değil
  // BİRLİKTE sayılıyor: aynı küçük harfli dize hem bir ürünü hem bir varyantı
  // gösteriyorsa hangisinin kastedildiği yine bilinmiyor.
  const refCount = new Map<string, number>();
  for (const p of products) {
    const k = p.platformRef.trim().toLowerCase();
    if (k) refCount.set(k, (refCount.get(k) ?? 0) + 1);
  }
  for (const v of variants) {
    const k = v.platformRef.trim().toLowerCase();
    if (k) refCount.set(k, (refCount.get(k) ?? 0) + 1);
  }

  for (const p of products) {
    index.productByRef.set(p.platformRef, p.id);
    const numeric = gidNumericTail(p.platformRef);
    if (numeric) index.productByNumeric.set(numeric, p.id);

    const lower = p.platformRef.trim().toLowerCase();
    if (!lower) continue;
    if ((refCount.get(lower) ?? 0) > 1) {
      index.ambiguousRefs.add(lower);
      index.productByRefLower.delete(lower);
      continue;
    }
    index.productByRefLower.set(lower, p.id);
  }

  const skuCount = new Map<string, number>();
  for (const v of variants) {
    const sku = v.sku?.trim().toLowerCase();
    if (sku) skuCount.set(sku, (skuCount.get(sku) ?? 0) + 1);
  }

  for (const v of variants) {
    const target: ProductRefTarget = { productId: v.productId, variantId: v.id };
    index.variantByRef.set(v.platformRef, target);
    const numeric = gidNumericTail(v.platformRef);
    if (numeric) index.variantByNumeric.set(numeric, target);

    const lowerRef = v.platformRef.trim().toLowerCase();
    if (lowerRef) {
      if ((refCount.get(lowerRef) ?? 0) > 1) {
        index.ambiguousRefs.add(lowerRef);
        index.variantByRefLower.delete(lowerRef);
      } else {
        index.variantByRefLower.set(lowerRef, target);
      }
    }

    const sku = v.sku?.trim().toLowerCase();
    if (!sku) continue;
    if ((skuCount.get(sku) ?? 0) > 1) {
      index.ambiguousSkus.add(sku);
      index.variantBySku.delete(sku);
      continue;
    }
    index.variantBySku.set(sku, target);
  }

  return index;
}

// ═══════════════════════════════════════════════════════════════════════════
// Adapter sınırı — ürün kırılımlı metrik
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Platformdan gelen ürün kırılımlı bir günlük satır.
 *
 * 🔴 `conversions`/`revenue` `null` OLABİLİR ve bu bir PLATFORM ASİMETRİSİ,
 * bizim eksiğimiz değil:
 *
 *  · Google `shopping_performance_view` ürün başına dönüşüm ve dönüşüm
 *    değeri VERİYOR.
 *  · Meta, `breakdowns=product_id` açıkken `actions`/`action_values`
 *    dizilerini VERMİYOR — ürün başına gelir okunamıyor.
 *
 * `0` yazmak, Meta'da satan bir ürünü "hiç satmadı" göstermek olurdu.
 * Ürünün GERÇEK cirosu zaten `order_lines`ta ve rapor ikisini birleştiriyor.
 */
export interface NormalizedProductInsight {
  /// Kampanyanın PLATFORM kimliği — bizim kimliğimiz senkronda çözülüyor.
  campaignPlatformRef: string;
  /// Feed'deki ham ürün kimliği (`retailer_id` / `product_item_id`).
  externalProductRef: string;
  /// `YYYY-MM-DD`.
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number | null;
  revenue: number | null;
  currency: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Katalog sağlığı — okuma sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

export const catalogHealthQuerySchema = z.object({
  storeConnectionId: z.uuid().optional(),
  /// Kaç ürün döneceği. Bu uç "en kötüler" listesi — tam katalog `/products`.
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /// Yalnız metinle düzeltilebilir sorunu olanlar (AI akışının giriş listesi).
  onlyFixable: z.coerce.boolean().default(false),
});
export type CatalogHealthQuery = z.infer<typeof catalogHealthQuerySchema>;

export interface CatalogHealthSummary {
  /**
   * 🔴 PUANI OLAN ürün sayısı — katalogdaki TÜM ürünler değil.
   *
   * `healthScore = null` "henüz değerlendirilmedi" demek ve ortalamaya
   * katılmıyor. Katılsaydı yeni senkronlanmış bir katalog, puanı düşük
   * değil ORTALAMASI ANLAMSIZ bir tablo üretirdi.
   */
  scoredProducts: number;
  /// Katalogdaki toplam ürün. `scoredProducts`tan büyük olması normal —
  /// aradaki fark "puanlanmayı bekleyen" demek ve arayüz bunu söyleyebilmeli.
  totalProducts: number;
  /// Puanı olan ürünlerin ortalaması. Ürün yoksa `null` — `0` "her ürün
  /// berbat" demek olurdu.
  averageScore: number | null;
  /// Kural kodu → o kuralı ihlal eden ürün sayısı. Azalan sıralı.
  issueCounts: { code: string; count: number }[];
  /// En düşük puanlı ürünler.
  worstProducts: {
    productId: string;
    title: string;
    imageUrl: string | null;
    healthScore: number;
    issues: string[];
    /// Metinle düzeltilebilenler — AI akışının hedefleyebileceği maddeler.
    fixableIssues: string[];
    scoredAt: string | null;
  }[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Ürün performans raporu
// ═══════════════════════════════════════════════════════════════════════════

export const productPerformanceQuerySchema = listQuerySchema.extend({
  from: z.iso.date(),
  to: z.iso.date(),
  storeConnectionId: z.uuid().optional(),
  /// `spend` ve `roas` YALNIZ reklam tarafı eşleşmiş ürünlerde anlamlı;
  /// sıralama eşleşmeyenleri sona atıyor (SQL'de `NULLS LAST`).
  sort: z.enum(['revenue', 'unitsSold', 'spend', 'roas', 'healthScore']).default('revenue'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type ProductPerformanceQuery = z.infer<typeof productPerformanceQuerySchema>;

export interface ProductPerformanceRow {
  productId: string;
  title: string;
  imageUrl: string | null;
  status: string;
  healthScore: number | null;

  // ── Mağaza tarafı — `order_lines` ────────────────────────────────────────
  /// Satılan adet ve ciro. İade DÜŞÜLMÜŞ değil: iade sipariş seviyesinde
  /// tutuluyor (`Order.totalRefunded`) ve hangi SATIRIN iade edildiği
  /// platformdan gelmiyor. Satır bazına dağıtmak uydurma olurdu.
  unitsSold: number;
  revenue: number;
  orderCount: number;

  // ── Reklam tarafı — `product_metrics_daily` ──────────────────────────────
  /// `null` = bu ürün için eşleşmiş reklam satırı YOK. `0` DEĞİL: sıfır
  /// harcama "reklam verildi, para harcanmadı" demek olurdu.
  spend: number | null;
  impressions: number | null;
  clicks: number | null;

  /**
   * 🔴 ROAS DEĞİL — adı bilerek `storeRoas`.
   *
   * Payda reklam harcaması (platform), pay MAĞAZA cirosu (`order_lines`).
   * İkisi AYNI atıf evreninden gelmiyor: mağaza cirosu o üründen yapılan TÜM
   * satışları içeriyor — organik, e-posta, doğrudan trafik dâhil. Yani bu
   * oran gerçek ROAS'tan YÜKSEK çıkar.
   *
   * Neden yine de veriliyor: ürünler ARASINDA karşılaştırma için doğru bir
   * sinyal (aynı yanlılık hepsinde var) ve alternatifi Meta'da hiçbir gelir
   * göstermemekti. Arayüzün bunu "ROAS" diye etiketlememesi ŞART.
   */
  storeRoas: number | null;
  matchStrategy: string | null;
}

export interface ProductPerformanceResponse {
  rows: ProductPerformanceRow[];
  total: number;
  /**
   * 🔴 KAPSAM BEYANI — `FunnelCoverage` ile aynı gerekçe.
   *
   * Ürün kırılımlı reklam verisi YALNIZ katalog tabanlı kampanyalarda var
   * (Meta Advantage+/DPA, Google Shopping/PMax). Normal trafik kampanyası
   * ürün kimliği DÖNDÜRMÜYOR — hata da vermiyor, sadece satır yok.
   *
   * Bu beyan olmadan kullanıcı "reklam harcamam yok" sanır; oysa harcama var,
   * ürüne bağlanamıyor. Sessiz kalmak, Faz 2'nin kapatmaya çalıştığı hata
   * sınıfının ta kendisi olurdu.
   */
  coverage: ProductAdCoverage;
}

export interface ProductAdCoverage {
  /// Ürüne bağlanabilen harcama.
  matchedSpend: number;
  /// Feed kimliği geldi ama katalogda karşılığı yok.
  unmatchedSpend: number;
  /// Ürün kırılımlı satır üreten kampanya sayısı.
  campaignsWithProductData: number;
  /// Hesaptaki toplam kampanya sayısı — oran buradan okunuyor.
  totalCampaigns: number;
  /// Ürün kırılımı okunabilen platformlar.
  platforms: string[];
  /**
   * Meta satırlarında gelir YOK (yukarıdaki asimetri). Arayüz bunu söylemek
   * zorunda, yoksa "Meta'da hiç satış yok" gibi okunur.
   */
  revenueMissingPlatforms: string[];
}
